import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { AuthContext } from "../context/AuthContext";

// ── Inline SVG Logo Icon (diamond + ECG waveform) ────────────────────────────
const MedimagicIcon = ({ size = 52 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M40 6 L74 40 L40 74 L6 40 Z"
      stroke="#3072FF"
      strokeWidth="5.5"
      fill="none"
      strokeLinejoin="round"
    />
    <polyline
      points="12,40 25,40 31,20 40,60 49,40 62,40 68,40"
      stroke="#43E0FF"
      strokeWidth="3.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Floating dot decorations (petel-style) ────────────────────────────────────
const Petel = ({ style, color = "#43E0FF", size = 17 }) => (
  <span
    className="petel-dot"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: color,
      position: "absolute",
      ...style,
    }}
  />
);

// ── Main Login Page ───────────────────────────────────────────────────────────
export default function LoginPage() {
  const { login, register } = useContext(AuthContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState("login"); // "login" | "register"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      if (!name.trim()) {
        setError("Please enter your name.");
        setLoading(false);
        return;
      }
      result = await register(name, email, password);
    }

    setLoading(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="login-page-root">
      {/* ── LEFT PANEL (dark navy) ─────────────────────────── */}
      <div className="login-left-panel">
        {/* Floating decorative dots */}
        <Petel style={{ top: 80,  right: 60  }} color="#43E0FF" size={35} />
        <Petel style={{ top: 200, left:  40  }} color="#9070FE" size={17} />
        <Petel style={{ top: 340, right: 40  }} color="#FE4A9E" size={17} />
        <Petel style={{ bottom: 200, left: 80 }} color="#43E0FF" size={22} />
        <Petel style={{ bottom: 100, right: 80 }} color="#9070FE" size={17} />
        <Petel style={{ bottom: 300, left: 160 }} color="#FE4A9E" size={14} />

        {/* Logo */}
        <div className="login-logo">
          <MedimagicIcon size={52} />
          <div className="login-logo-text">
            <span className="logo-name">medimagic</span>
            <span className="logo-sub">dashboard</span>
          </div>
        </div>

        {/* Brand heading */}
        <div className="login-brand-heading">
          <p className="login-brand-label">Welcome to</p>
          <h1 className="login-brand-h1">
            <span className="login-brand-line">Revolutionizing</span>
            <span className="login-brand-line">medical</span>
            <span className="login-brand-line highlight-line">education</span>
          </h1>
          <p className="login-brand-sub">
            World's first immersive 3D learning platform for medical professionals.
          </p>
        </div>

        {/* Stats row */}
        <div className="login-stats">
          <div className="login-stat">
            <span className="login-stat-num">200K+</span>
            <span className="login-stat-label">Students</span>
          </div>
          <div className="login-stat-divider" />
          <div className="login-stat">
            <span className="login-stat-num">500+</span>
            <span className="login-stat-label">Colleges</span>
          </div>
          <div className="login-stat-divider" />
          <div className="login-stat">
            <span className="login-stat-num">150h</span>
            <span className="login-stat-label">Content</span>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL (white form) ───────────────────────── */}
      <div className="login-right-panel">
        <div className="login-form-wrap">
          {/* Heading with MediMagic "half-underline" style */}
          <div className="login-form-header">
            <h2 className="login-form-title">
              {mode === "login" ? "Sign In." : "Sign Up."}
            </h2>
            <p className="login-form-sub">
              {mode === "login"
                ? "Enter your credentials to access your dashboard."
                : "Create your MediMagic Dashboard account."}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="login-error" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {/* Name (register only) */}
            {mode === "register" && (
              <div className="login-field">
                <label className="login-label" htmlFor="name">Full Name</label>
                <input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  className="login-input"
                  placeholder="Dr. Jane Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            )}

            {/* Email */}
            <div className="login-field">
              <label className="login-label" htmlFor="email">Email Address</label>
              <input
                id="email"
                data-testid="email-input"
                type="email"
                className="login-input"
                placeholder="you@medimagic.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Password */}
            <div className="login-field">
              <label className="login-label" htmlFor="password">Password</label>
              <div className="login-input-wrapper">
                <input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  className="login-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-password-btn"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            {mode === "login" && (
              <div className="login-forgot">
                <a href="#forgot" className="login-link" data-testid="forgot-password-link">
                  Forgot password?
                </a>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="login-btn"
              data-testid="login-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <span className="login-spinner" />
              ) : mode === "login" ? (
                "Log In"
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {/* Mode toggle */}
          <p className="login-toggle-text">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              type="button"
              className="login-link login-toggle-btn"
              data-testid="toggle-mode-btn"
              onClick={() => {
                setMode((m) => (m === "login" ? "register" : "login"));
                setError("");
              }}
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
